import '@xyflow/react/dist/style.css';
import { FlowNodes } from 'js/components/pipelines/FlowNodes';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { mapValues, pick } from 'lodash';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import RagFlowIdProvider from 'new/components/flows/types/genai_rag/context/RagFlowId/RagFlowIdProvider';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { APIUsageContextProvider } from 'new/components/flows/types/genai_rag/context/APIUsage/APIUsageContextProvider';
import { Stack } from 'new/elements/Stack';
import { useHistory } from 'react-router-dom';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { Button } from 'new/elements/Button';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { ReactFlowInstance } from '@xyflow/react';

const RAGFlowNodes = ({
  rootNodeId,
  children = null,
  toolbarItems = undefined,
  layoutDirection = undefined,
}) => {
  const entities = useSelector((state: any) => state.entities);
  const { flowNodes, dataSets } = entities;
  const branches = useSelector((state: any) => state.pipelines.branches);
  const branchItems = branches[rootNodeId]?.items;
  const history = useHistory();

  const { draftState, isEdit } = useDraftContext();

  const pipelineItems = useMemo(() => {
    const items = mapValues(pick(flowNodes, branchItems), (flowNode) =>
      getResourceEntity(flowNode, entities),
    );

    Object.values(items).forEach((item) => {
      if (item.node_type === 'data_retriever_holder') {
        // show full flows starting from a source

        let draftFlowLinks = [];
        (
          draftState?.flowLinks?.[item.id] || flowNodes[item.origin_node_id]?.linked_flows
        )?.forEach?.((flowNodeId) => {
          if (draftState?.flowsPendingDeletion?.length) {
            if (!draftState?.flowsPendingDeletion?.includes?.(flowNodeId)) {
              draftFlowLinks.push(flowNodeId);
            }
          } else {
            draftFlowLinks.push(flowNodeId);
          }
        });

        const linkedFlowIds = draftFlowLinks.filter(
          (linkedFlowId) => !flowNodes[linkedFlowId]?.data_retriever_ids?.length,
        );
        const linkedBranchItems =
          linkedFlowIds?.map((linkedFlowId) => branches[linkedFlowId]?.items) || [];

        // show data set retrievers
        const linkedDataSetIds =
          flowNodes[item.origin_node_id]?.linked_flows?.reduce((acc, linkedFlowId) => {
            const linkedFlowNode = flowNodes[linkedFlowId];
            if (linkedFlowNode?.data_retriever_ids?.length) {
              linkedFlowNode.data_retriever_ids.forEach((dataSetId) => {
                if (!draftState?.dataSetsPendingDeletion?.includes?.(dataSetId)) {
                  acc.push(dataSetId);
                }
              });
            }

            return acc;
          }, []) || [];

        draftState.dataSetsPendingLinking?.forEach?.((dataSetId) => {
          if (!draftState?.dataSetsPendingDeletion?.includes?.(dataSetId)) {
            linkedDataSetIds.push(dataSetId);
          }
        });

        linkedDataSetIds.forEach((dataSetId) => {
          const { flow_node_id } = dataSets[dataSetId];
          const dataSetBranch = [flow_node_id];
          const reranker = flowNodes[flow_node_id]?.children?.find(
            (childId) => flowNodes[childId]?.node_type === 'reranker',
          );

          if (reranker) {
            dataSetBranch.push(reranker);
          }

          if (!linkedBranchItems.some((flowIds) => flowIds?.includes(flow_node_id))) {
            linkedBranchItems.push(dataSetBranch);
          }
        });

        item.retriever_flows = linkedBranchItems.map((retrieverBranch) =>
          mapValues(pick(flowNodes, retrieverBranch), (flowNode) =>
            getResourceEntity(flowNode, entities),
          ),
        );
      }

      if (item.node_type === 'llm_processor') {
        const response = {
          ...item,
          flow_node_id: `${item.flow_node_id}-response`,
          node_type: 'query_response',
        };
        item.children = [response.flow_node_id];
        items[response.flow_node_id] = response;
      }
    });

    return items;
  }, [rootNodeId, branches, flowNodes, draftState]);

  const onEdit = () => {
    history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/${rootNodeId}/edit`);
  };

  return (
    <RagFlowIdProvider flowId={rootNodeId}>
      <APIUsageContextProvider>
        <FlowNodes
          id={rootNodeId}
          hideMiniMap
          keepFlowsOrder
          toolbarItems={toolbarItems}
          pipelineItems={pipelineItems}
          layoutDirection={layoutDirection}
        >
          {children}
          {!isEdit && (
            <Stack
              className="nodrag nowheel"
              zIndex={99}
              position="absolute"
              top="16px"
              right="16px"
            >
              <Button
                style={{ backgroundColor: 'white' }}
                onClick={onEdit}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            </Stack>
          )}
        </FlowNodes>
      </APIUsageContextProvider>
    </RagFlowIdProvider>
  );
};

export default memo(RAGFlowNodes);
